import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import StudySelectPage from "./pages/StudySelectPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import ComplianceCenterPage from "./pages/ComplianceCenterPage";
import TMFHealthPage from "./pages/TMFHealthPage";
import APIIntegrationPage from "./pages/APIIntegrationPage";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import EmailIngestionPage from "./pages/EmailIngestionPage";
import ReportsPage from "./pages/ReportsPage";
import SchedulerPage from "./pages/SchedulerPage";
import ScheduledReportsPage from "./pages/ScheduledReportsPage";
import ETMFManagementPage from "./pages/ETMFManagementPage";
import AssistantBot from "./components/AssistantBot";
import { Loader2 } from "lucide-react";

// Protected Route Component - requires auth AND study selection
const ProtectedRoute = ({ children, requireStudy = true }) => {
  const { isAuthenticated, loading, hasStudySelected } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // If study selection is required and not selected, redirect to study select
  if (requireStudy && !hasStudySelected) {
    return <Navigate to="/study-select" replace />;
  }

  return children;
};

// Study Select Route - requires auth but no study selection
const StudySelectRoute = ({ children }) => {
  const { isAuthenticated, loading, hasStudySelected } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // If study already selected, redirect to dashboard
  if (hasStudySelected) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to study select if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, hasStudySelected } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect to study select if no study selected, otherwise to dashboard
    return <Navigate to={hasStudySelected ? "/dashboard" : "/study-select"} replace />;
  }

  return children;
};

// Bot wrapper that only shows when authenticated and has study selected
const AuthenticatedBot = () => {
  const { isAuthenticated, hasStudySelected } = useAuth();
  
  if (!isAuthenticated || !hasStudySelected) return null;
  
  return <AssistantBot />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/study-select"
        element={
          <StudySelectRoute>
            <StudySelectPage />
          </StudySelectRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <ProtectedRoute>
            <ComplianceCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tmf-health"
        element={
          <ProtectedRoute>
            <TMFHealthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-integration"
        element={
          <ProtectedRoute>
            <APIIntegrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notification-settings"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-ingestion"
        element={
          <ProtectedRoute>
            <EmailIngestionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheduler"
        element={
          <ProtectedRoute>
            <SchedulerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheduled-reports"
        element={
          <ProtectedRoute>
            <ScheduledReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/etmf-management"
        element={
          <ProtectedRoute>
            <ETMFManagementPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter basename="/">
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <AuthenticatedBot />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
